import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';

interface Tarefa { id: string; categoria_id: string | null; categoria: string | null; texto: string; ordem: number; ativo: boolean; versao: number }
interface Categoria { id: string; codigo: string; nome: string; pai_id: string | null }

/**
 * Tarefas rápidas: frases que o técnico toca no app para montar o relato ao concluir (plantão sem digitar).
 * Por sistema (Automação de portões, CFTV…) ou gerais. O app mostra as do sistema da OS + as gerais.
 */
export function TarefasRapidas() {
  const { usuario } = useSessao();
  const podeEditar = pode(usuario, 'checklist.editar');
  const [lista, setLista] = useState<Tarefa[] | null>(null);
  const [cats, setCats] = useState<Categoria[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState({ texto: '', categoria_id: '' });
  const [edit, setEdit] = useState<Record<string, string>>({});

  const carregar = useCallback(() => { api<Tarefa[]>('/tarefas-rapidas').then(setLista).catch((e) => setErro(e.message)); }, []);
  useEffect(() => { carregar(); api<Categoria[]>('/categorias-ativo').then((c) => setCats(c.filter((x) => !x.pai_id))).catch(() => {}); }, [carregar]);

  async function criar() {
    setErro(null);
    try { await api('/tarefas-rapidas', { metodo: 'POST', corpo: { texto: novo.texto.trim(), categoria_id: novo.categoria_id || null } }); setNovo({ texto: '', categoria_id: novo.categoria_id }); carregar(); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível incluir.'); }
  }
  async function salvar(t: Tarefa, mudanca: Record<string, unknown>) {
    setErro(null);
    try { await api(`/tarefas-rapidas/${t.id}`, { metodo: 'PATCH', corpo: { versao: t.versao, ...mudanca } }); setEdit((e) => { const n = { ...e }; delete n[t.id]; return n; }); carregar(); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível salvar.'); }
  }

  const grupos = new Map<string, Tarefa[]>();
  for (const t of lista ?? []) { const k = t.categoria ?? 'Gerais (qualquer sistema)'; grupos.set(k, [...(grupos.get(k) ?? []), t]); }

  return (
    <>
      <Link to="/checklists" className="voltar"><ArrowLeft size={16} aria-hidden /> Checklists</Link>
      <div className="cabecalho-pagina">
        <div><span className="kicker">Plantão rápido</span><h1 className="titulo">Tarefas rápidas</h1></div>
      </div>
      <p className="nota bloco-explica">
        Na hora de concluir a OS, o técnico toca nas tarefas feitas e o relato se monta sozinho. Aparecem as tarefas do sistema da OS
        (o técnico escolhe o sistema ao abrir a corretiva de plantão) e as gerais. Escreva como o cliente vai ler no relatório.
      </p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {podeEditar && (
        <div className="cartao form" style={{ marginBottom: 16 }}>
          <div className="linha-3-igual">
            <label>Sistema
              <select value={novo.categoria_id} onChange={(e) => setNovo({ ...novo, categoria_id: e.target.value })}>
                <option value="">Geral (qualquer sistema)</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </label>
            <label style={{ gridColumn: 'span 2' }}>Tarefa<input value={novo.texto} onChange={(e) => setNovo({ ...novo, texto: e.target.value })} placeholder="Ex.: Trocado capacitor do motor" maxLength={200} /></label>
          </div>
          <div className="acoes-form"><button className="btn" disabled={novo.texto.trim().length < 3} onClick={criar}><Plus size={18} aria-hidden /> Incluir</button></div>
        </div>
      )}
      {!lista ? <p className="nota">Carregando…</p> : [...grupos.entries()].map(([g, ts]) => (
        <section key={g} className="cartao tabela-cartao" style={{ marginBottom: 16 }}>
          <h2 style={{ padding: '14px 16px 0' }}>{g}</h2>
          <table className="tabela">
            <tbody>
              {ts.map((t) => (
                <tr key={t.id} className={t.ativo ? '' : 'inativo'}>
                  <td>{edit[t.id] !== undefined ? <input value={edit[t.id]} onChange={(e) => setEdit({ ...edit, [t.id]: e.target.value })} maxLength={200} style={{ width: '100%' }} /> : t.texto}</td>
                  {podeEditar && <td className="acoes-linha">
                    {edit[t.id] !== undefined ? <>
                      <button className="btn-link" disabled={edit[t.id]!.trim().length < 3} onClick={() => salvar(t, { texto: edit[t.id]!.trim() })}>salvar</button>
                      <button className="btn-link" onClick={() => setEdit((x) => { const n = { ...x }; delete n[t.id]; return n; })}>cancelar</button>
                    </> : <>
                      <button className="btn-link" onClick={() => setEdit({ ...edit, [t.id]: t.texto })}>editar</button>
                      <button className="btn-link" onClick={() => salvar(t, { ativo: !t.ativo })}>{t.ativo ? 'desativar' : 'reativar'}</button>
                    </>}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </>
  );
}
